/**
 * Intro offer, once per client (Pilates studio shape, M1; migration 0070). A package with
 * `newClientsOnly` is a starter pack: only a new client can buy it, one time, and the shop shows it
 * first. A new client is an account with no earlier purchase and no attended session at the business
 * (`packages/core/src/services/intro-offer.ts` decides it). Pure helpers that the API, Desk web and
 * the mobile app share.
 */

type ShopProduct = { newClientsOnly: boolean; priceFils: number };

/** Who may buy a package, as the analytics events say it. */
export type PackageAudience = "new_clients" | "all";

export function packageAudienceOf(product: { newClientsOnly: boolean }): PackageAudience {
  return product.newClientsOnly ? "new_clients" : "all";
}

/**
 * What the member shop lists, in its order: an intro offer only for a new client, and first;
 * then every other package, the cheapest first.
 */
export function shopProducts<T extends ShopProduct>(products: readonly T[], newClient: boolean): T[] {
  return products
    .filter((product) => newClient || !product.newClientsOnly)
    .sort((a, b) => Number(b.newClientsOnly) - Number(a.newClientsOnly) || a.priceFils - b.priceFils);
}

/** The tag on an intro offer in the shop: "New client offer". `member` is the business's word for a member. */
export function newClientOfferTag(member: string): string {
  return `New ${member.toLowerCase()} offer`;
}

/** The owner's name for the setting and the package: "New clients only". `members` is the business's plural word. */
export function newClientsOnlyLabel(members: string): string {
  return `New ${members.toLowerCase()} only`;
}

/** The help line under the owner's "New clients only" setting. */
export const NEW_CLIENTS_ONLY_HELP =
  "Only a person with no earlier purchase and no attended session here can buy it. Each person can buy it one time.";
